import { Controller } from '@hotwired/stimulus'
import { i18n } from 'config/translations'

export default class extends Controller {
  static targets = [
    'form',
    'cardNumberError',
    'cardExpireError',
    'cardCVCError',
    'submit',
    'loadingCoupon',
    'totalFee',
    'couponInModal',
    'couponInSummary',
    'cinform',
    'addCouponButton',
    'couponInput',
    'loading',
    'discountId',
    'discountName',
    'couponInModalValue',
    'displayAmount',
    'agreeError',
  ]

  static values = { stripeKey: String, stripeClientSecret: String, total: String }

  confirmCardSetup(stripe, cardNumber) {
    const self = this
    stripe
      .confirmCardSetup(this.stripeClientSecretValue, {
        payment_method: {
          card: cardNumber,
        },
      })
      .then(function (result) {
        self.cardNumberErrorTarget.textContent = ''
        self.cardExpireErrorTarget.textContent = ''
        self.cardCVCErrorTarget.textContent = ''
        self.element.querySelector('#card-number').classList.remove('border-red-600', 'bg-red-50')
        self.element.querySelector('#card-expiry').classList.remove('border-red-600', 'bg-red-50')
        self.element.querySelector('#card-cvc').classList.remove('border-red-600', 'bg-red-50')
        self.element.querySelector('#card-label').classList.remove('text-red-600')
        self.element.querySelector('#exdate-label ').classList.remove('text-red-600')
        self.element.querySelector('#cvv-label').classList.remove('text-red-600')
        self.element.querySelector('#stripe-3ds-error').classList.add('hidden')
        if (result.error) {
          if (result.error.code === 'incomplete_number') {
            self.cardNumberErrorTarget.textContent = i18n.t('message.error.card.number.incorrect')
            self.element.querySelector('#card-number').classList.add('border-red-600', 'bg-red-50')
            self.element.querySelector('#card-label').classList.add('text-red-600')
          } else if (result.error.code === 'invalid_number') {
            self.cardNumberErrorTarget.textContent = i18n.t('message.error.card.number.incorrect')
            self.element.querySelector('#card-number').classList.add('border-red-600', 'bg-red-50')
            self.element.querySelector('#card-label').classList.add('text-red-600')
          } else if (result.error.code === 'incomplete_expiry') {
            self.cardExpireErrorTarget.textContent = i18n.t('message.error.card.expiration_date.incorrect')
            self.element.querySelector('#card-expiry').classList.add('border-red-600', 'bg-red-50')
            self.element.querySelector('#exdate-label').classList.add('text-red-600')
          } else if (result.error.code === 'invalid_expiry_year_past') {
            self.cardExpireErrorTarget.textContent = i18n.t('message.error.card.expiration_date.expired')
            self.element.querySelector('#card-expiry').classList.add('border-red-600', 'bg-red-50')
            self.element.querySelector('#exdate-label').classList.add('text-red-600')
          } else if (result.error.code === 'incomplete_cvc') {
            self.cardCVCErrorTarget.textContent = i18n.t('message.error.card.security_code.incorrect')
            self.element.querySelector('#card-cvc').classList.add('border-red-600', 'bg-red-50')
            self.element.querySelector('#cvv-label').classList.add('text-red-600')
          } else {
            self.element.querySelector('#stripe-3ds-error').classList.remove('hidden')
            window.scrollTo({ top: 0, behavior: 'smooth' })
          }

          self.enableClick()
        } else {
          self.stripePaymentMethodHandler(result.setupIntent.payment_method)
        }
        return
      })
      .catch(() => {})
  }

  stripePaymentMethodHandler(paymentMethod) {
    const hiddenInput = document.createElement('input')
    hiddenInput.setAttribute('type', 'hidden')
    hiddenInput.setAttribute('name', 'fronts_confirm[payment_method]')
    hiddenInput.setAttribute('value', paymentMethod)

    // リクエスト前にボタンを切り替えて擬似ローディング処理を行う
    const reservationButtonAll = this.element.querySelectorAll('.confirm-submit')
    const requestButtonAll = this.element.querySelectorAll('.request-processing')
    reservationButtonAll.forEach((el) => {
      el.classList.add('md:hidden', 'hidden')
    })
    requestButtonAll.forEach((el) => {
      el.classList.add('md:inline-block', '!inline-block')
    })

    this.formTarget.appendChild(hiddenInput)
    this.formTarget.submit()
  }

  connect() {
    // eslint-disable-next-line no-undef
    const stripe = Stripe(this.stripeKeyValue)
    const elements = stripe.elements()

    const elementStyles = {
      base: {
        fontFamily: 'sans-serif',
        lineHeight: '20px',
        height: '42px',
        fontSize: '14px',
        '::placeholder': {
          color: '#aaa',
        },
        ':-webkit-autofill': {
          color: '#e39f48',
        },
      },
      invalid: {
        color: 'red',
      },
    }

    const cardNumber = elements.create('cardNumber', {
      style: elementStyles,
      classes: {
        base: 'text-sm h-10 w-full bg-gray-50 border border-gray-300 rounded-lg px-4 py-1 mt-2 required number-only md:w-[353px]',
      },
    })

    const cardExpiry = elements.create('cardExpiry', {
      style: elementStyles,
      classes: {
        base: 'h-10 text-sm w-full bg-gray-50 border border-gray-300 rounded-lg px-4 py-1 mt-2 required expire-date',
      },
    })

    const cardCvc = elements.create('cardCvc', {
      style: elementStyles,
      classes: {
        base: 'text-sm h-10 w-full bg-gray-50 border border-gray-300 rounded-lg px-4 py-1 mt-2 required number-only',
      },
    })

    cardNumber.mount('#card-number')
    cardExpiry.mount('#card-expiry')
    cardCvc.mount('#card-cvc')

    const self = this
    this.formTarget.addEventListener('submit', function (e) {
      e.preventDefault()
      self.disableClick()
      self.confirmCardSetup(stripe, cardNumber)
    })

    this.formTarget.querySelectorAll('.__PrivateStripeElement').forEach((el) => {
      el.classList.add('mt-1')
      el.style.margin = null
    })
  }

  disableClick() {
    const submits = this.element.querySelectorAll('.confirm-submit')
    submits.forEach((el) => el.classList.add('text-colorBase', 'bg-gray-200', 'pointer-events-none'))
  }

  enableClick() {
    const submits = this.element.querySelectorAll('.confirm-submit')
    submits.forEach((el) => el.classList.remove('text-colorBase', 'bg-gray-200', 'pointer-events-none'))
  }

  toggleButton() {
    const isChecked = this.element.querySelector('#agree-checkbox').checked

    if (isChecked) {
      this.agreeErrorTarget.textContent = ''
      this.enableClick()
    } else {
      this.agreeErrorTarget.textContent = i18n.t('reservation_confirm.information.agree.notes')
      this.disableClick()
    }
  }

  couponSubmit(event) {
    event.preventDefault()
    const self = this
    this.loadingCouponTarget.classList.remove('hidden')
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
    fetch('/referral_codes/coupon-check', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        'X-CSRF-Token': csrfToken,
        Accept: 'application/json',
      },
      body: 'code=' + encodeURIComponent(self.couponInputTarget.value),
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.code === 'er') {
          alert(data.message)
        }
        if (data.code === 'ok') {
          if (self.couponInputTarget.value) {
            self.couponInModalTarget.classList.remove('hidden')
            self.couponInSummaryTarget.classList.remove('hidden')
            self.cinformTarget.classList.remove('hidden')
            self.cinformTarget.textContent = i18n.t('reservation_confirm.promotion.applied', {
              code: self.couponInputTarget.value,
            })
            self.couponInSummaryTarget.textContent =
              i18n.t('reservation_confirm.promotion.discount') +
              i18n.t('reservation_confirm.promotion.amount', {
                amount: self.addCommas(data.discount_amount),
              })
            self.discountIdTarget.value = data.discount_master_id
            self.discountNameTarget.value = data.discount_master_name
            self.couponInModalValueTarget.textContent = i18n.t('reservation_confirm.promotion.amount', {
              amount: self.addCommas(data.discount_amount),
            })
            const newTotalFee = i18n.t('price_text', {
              price: self.addCommas((parseInt(self.totalValue) - parseInt(data.discount_amount)).toString()),
            })
            self.totalFeeTargets[0].textContent = newTotalFee
            self.totalFeeTargets[1].textContent = newTotalFee
            self.displayAmountTarget.value = parseInt(self.totalValue) - parseInt(data.discount_amount)
          }
        }
        self.loadingCouponTarget.classList.add('hidden')
        return undefined
      })
      .catch(() => {})
  }

  addCommas(number) {
    return number.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
  }

  couponKeyupChange(event) {
    if (event.target.value) {
      this.addCouponButtonTarget.classList.remove('text-gray-200', 'border-gray-200', 'cursor-default')
      this.addCouponButtonTarget.classList.add('text-black', 'border-black', 'cursor-pointer')
    } else {
      this.addCouponButtonTarget.classList.add('text-gray-200', 'border-gray-200', 'cursor-pointer')
      this.addCouponButtonTarget.classList.remove('text-black', 'border-black', 'cursor-default')
    }
  }
}
